/**
 * App shell — collapsible sidebar nav + content area.
 *
 * Layout:
 *   [ sidebar ][ main content                                                ]
 *   [ collap- ][ active view (e.g. softphone + reserved Cerebro panel)       ]
 *
 * Sidebar collapses to icons-only by clicking the chevron. State persists in
 * localStorage so it survives reloads.
 *
 * Routing is `useState<View>` for MVP; swap to TanStack Router once we exceed
 * ~8 views or add deep linking.
 */

import { useEffect, useState } from "react";
import {
  Phone, Headphones, BarChart3, Settings, Users2, ListChecks, LineChart, LogOut,
  Voicemail as VoicemailIcon, Activity, ChevronLeft, ChevronRight,
} from "lucide-react";
import { can, ROLE_LABELS, type AppRole } from "../lib/roles";
import { SoftphoneView } from "../views/SoftphoneView";
import { VoicemailsView } from "../views/VoicemailsView";
import { ActivityView } from "../views/ActivityView";
import { UsersView } from "../views/UsersView";
import { TenantsView } from "../views/TenantsView";
import { Placeholder } from "../views/Placeholder";

type View =
  | "softphone" | "voicemails" | "liveQueues" | "routing" | "scheduling"
  | "reports" | "activity" | "adminUsers" | "adminTenant";

type NavEntry = {
  key: View;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  cap: Parameters<typeof can>[1];
};

const NAV: NavEntry[] = [
  { key: "softphone",   label: "Softphone",   icon: Phone,         cap: "softphone" },
  { key: "voicemails",  label: "Voicemails",  icon: VoicemailIcon, cap: "voicemails" },
  { key: "liveQueues",  label: "Live queues", icon: Headphones,    cap: "liveQueues" },
  { key: "routing",     label: "Routing",     icon: ListChecks,    cap: "routing" },
  { key: "scheduling",  label: "Scheduling",  icon: BarChart3,     cap: "scheduling" },
  { key: "reports",     label: "Reports",     icon: LineChart,     cap: "reports" },
  { key: "activity",    label: "Activity",    icon: Activity,      cap: "activity" },
  { key: "adminUsers",  label: "Users",       icon: Users2,        cap: "adminUsers" },
  { key: "adminTenant", label: "Tenant",      icon: Settings,      cap: "adminTenant" },
];

const COLLAPSED_KEY = "rogue.sidebar.collapsed";

export function Shell({
  me,
  onSignOut,
}: {
  me: {
    id: string; email: string; displayName?: string; role: AppRole; tenantId: string;
    directDialNumber?: string | null;
  };
  onSignOut: () => void;
}) {
  const visible = NAV.filter((n) => can(me.role, n.cap));
  const [view, setView] = useState<View>(visible[0]?.key ?? "reports");
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(COLLAPSED_KEY);
    if (saved === "1") setCollapsed(true);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside
        className={[
          "bg-rogue-900 text-rogue-100 flex flex-col transition-[width] duration-200 ease-out",
          collapsed ? "w-[clamp(3rem,5vmin,5rem)]" : "w-[clamp(14rem,18vmin,18rem)]",
        ].join(" ")}
      >
        <div className="pad-fluid border-b border-rogue-800 flex items-center justify-between gap-2">
          {!collapsed && (
            <div>
              <div className="text-xl font-bold text-white">Atticor-Rogue</div>
              <div className="text-xs text-rogue-400 mt-1">{ROLE_LABELS[me.role]}</div>
            </div>
          )}
          <button
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            className="p-2 rounded hover:bg-rogue-800 text-rogue-300 hover:text-white shrink-0"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 pad-fluid gap-fluid flex flex-col">
          {visible.map((n) => {
            const Icon = n.icon;
            const active = n.key === view;
            return (
              <button
                key={n.key}
                onClick={() => setView(n.key)}
                title={collapsed ? n.label : undefined}
                aria-label={n.label}
                className={[
                  "flex items-center rounded-lg pad-fluid text-left transition-colors",
                  collapsed ? "justify-center" : "gap-3",
                  active ? "bg-rogue-700 text-white" : "hover:bg-rogue-800 text-rogue-200",
                ].join(" ")}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && <span className="truncate">{n.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="pad-fluid border-t border-rogue-800">
          {!collapsed && (
            <>
              <div className="text-sm font-medium truncate">{me.displayName || me.email}</div>
              <div className="text-xs text-rogue-400 truncate">{me.email}</div>
            </>
          )}
          <button
            onClick={onSignOut}
            title={collapsed ? "Sign out" : undefined}
            className={[
              "mt-3 flex items-center text-xs text-rogue-300 hover:text-white",
              collapsed ? "justify-center w-full" : "gap-2",
            ].join(" ")}
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-auto bg-rogue-50">
        {view === "softphone"   && <SoftphoneView me={me} />}
        {view === "voicemails"  && <VoicemailsView />}
        {view === "liveQueues"  && <Placeholder title="Live queues" />}
        {view === "routing"     && <Placeholder title="Routing configuration" />}
        {view === "scheduling"  && <Placeholder title="Scheduling" />}
        {view === "reports"     && <Placeholder title="Reports" />}
        {view === "activity"    && <ActivityView />}
        {view === "adminUsers"  && <UsersView me={me} />}
        {view === "adminTenant" && <TenantsView />}
      </main>
    </div>
  );
}
